// githubTool.js — read the user's GitHub projects as an agent + voice tool.
//
// The Worker already exposes /api/github/repos (all repos, newest activity first,
// via the stored GitHub OAuth token) but nothing surfaced it to the agent or the
// voice session — so "quais projetos eu tenho no GitHub?" / "sobre o que é o repo
// X?" had no data. This closes that: read-only, formatted for speech + chat.
//
// Read-only: it never creates/edits repos or PRs (those stay in the text UI).

import { apiFetch } from './api.js';

// ── Pure formatting (model-facing) ───────────────────────────────────────
export function formatGithubProjects(repos, query) {
  const list = Array.isArray(repos) ? repos : [];
  const head = query ? `[github_projects "${query}"]` : '[github_projects]';
  if (!list.length) {
    return query ? `${head} Nenhum projeto casa "${query}".` : `${head} Nenhum projeto encontrado.`;
  }
  const lines = list.map((r) => {
    const flags = [r.private ? 'privado' : null, r.fork ? 'fork' : null, r.archived ? 'arquivado' : null]
      .filter(Boolean)
      .join(', ');
    const meta = [r.language, flags].filter(Boolean).join(' · ');
    const desc = r.description ? ` — ${r.description}` : '';
    const when = r.updated_at ? ` (atualizado ${String(r.updated_at).slice(0, 10)})` : '';
    return `• ${r.full_name || r.name}${desc}${meta ? ` [${meta}]` : ''}${when}`;
  });
  return `${head} ${list.length} projeto(s), mais recentes primeiro:\n${lines.join('\n')}`;
}

// Filter + cap client-side so voice output stays short even when the account has
// hundreds of repos.
export function selectRepos(repos, { query = '', max = 12 } = {}) {
  const list = Array.isArray(repos) ? repos : [];
  const q = String(query || '').trim().toLowerCase();
  const filtered = q
    ? list.filter((r) =>
        `${r.full_name || ''} ${r.name || ''} ${r.description || ''} ${r.language || ''}`
          .toLowerCase()
          .includes(q),
      )
    : list;
  return filtered.slice(0, Math.min(Math.max(1, max), 25));
}

/** Executor (never throws; returns a model-facing string). */
export async function executeGithubProjects(args) {
  const q = typeof args?.q === 'string' ? args.q.trim() : '';
  const max = Number.parseInt(args?.max, 10);
  const res = await apiFetch('/api/github/repos');
  if (!res.ok) {
    const code = res.data?.code;
    if (code === 'GITHUB_NOT_CONNECTED') return 'Erro: GitHub não conectado — conecte em Integrações.';
    return `Erro: não consegui ler seus projetos do GitHub (${res.error || 'rede/auth'}).`;
  }
  const repos = selectRepos(res.data?.repos, { query: q, max: Number.isFinite(max) ? max : 12 });
  return formatGithubProjects(repos, q);
}

// ── Agent-loop tool (extraTools) ─────────────────────────────────────────
const GITHUB_PROJECTS_DEF = {
  type: 'function',
  function: {
    name: 'github_projects',
    description:
      'Lista os repositórios do GitHub do usuário (somente leitura), mais recentes primeiro — nome, descrição, linguagem, visibilidade e data. '
      + 'Use para "quais projetos eu tenho", "sobre o que é o repo X", entender no que ele trabalha. Filtre por texto com {"q":"apolo"}. '
      + 'Requer GitHub conectado em Integrações.',
    parameters: {
      type: 'object',
      properties: {
        q: { type: 'string', description: 'filtro por nome/descrição/linguagem (opcional)' },
        max: { type: 'integer', description: 'quantos repos (1-25, padrão 12)' },
      },
    },
  },
};

/**
 * extraTool resolver: GitHub read-only, available in local sessions (chat E code).
 * Fora do browser → [] (contrato dos extraTools). Se não estiver conectado, o Worker
 * devolve a orientação de conectar, que o modelo repassa.
 */
export function resolveGithubTools() {
  if (typeof window === 'undefined') return [];
  return [{ def: GITHUB_PROJECTS_DEF, execute: (a) => executeGithubProjects(a) }];
}
